import { memo, useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, ShieldCheck, X } from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';
import { RotatingLoader } from '@/components/ui/rotating-loader';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import type {
  RolesPermissionColumn,
  RolesPermissionGroup,
} from './processPermissionData';

interface RolesPermissionTableProps {
  columns: RolesPermissionColumn[];
  groups: RolesPermissionGroup[];
  isLoading: boolean;
}

const HEADER_CELL = 'sticky top-0 h-11 px-4 align-middle bg-background border-b border-border';
const STICKY_LEFT_CORNER = 'sticky left-0 z-30 min-w-[220px]';
const STICKY_TOP_COL = 'z-20 min-w-[160px] text-center';
const GROUP_LEFT = 'sticky left-0 z-20 bg-muted min-w-[220px]';
const ROW_LEFT = 'sticky left-0 z-10 bg-background min-w-[220px]';

const RolesPermissionTable = memo(function RolesPermissionTable({
  columns,
  groups,
  isLoading,
}: RolesPermissionTableProps) {
  const [openState, setOpenState] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(groups.map((g) => [g.id, true])),
  );

  // Merge in newly-arrived groups without clobbering user toggles for
  // groups that persist across refetches.
  useEffect(() => {
    setOpenState((prev) => {
      const next: Record<number, boolean> = {};
      for (const group of groups) {
        next[group.id] = prev[group.id] ?? true;
      }
      return next;
    });
  }, [groups]);

  const toggleGroup = (id: number) =>
    setOpenState((prev) => ({ ...prev, [id]: !prev[id] }));

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center">
        <RotatingLoader size={32} />
      </div>
    );
  }

  if (groups.length === 0 || columns.length === 0) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="No Roles to Display"
        description="There are no roles or permissions to show for this organization."
      />
    );
  }

  return (
    <table className="w-full caption-bottom border-collapse text-sm">
      <thead>
        <tr>
          <th scope="col" className={cn(HEADER_CELL, STICKY_LEFT_CORNER, 'text-left')}>
            <Typography variant="small-semibold" className="uppercase tracking-wide text-muted-foreground">
              Resource / Action
            </Typography>
          </th>
          {columns.map((column) => (
            <th
              key={`${column.id}-${column.dataIdentifier}`}
              scope="col"
              className={cn(HEADER_CELL, STICKY_TOP_COL)}
            >
              <Typography variant="small-semibold" className="uppercase tracking-wide text-muted-foreground">
                {column.name}
              </Typography>
            </th>
          ))}
        </tr>
      </thead>
      {groups.map((group) => {
        const isOpen = openState[group.id] ?? false;
        return (
          <tbody key={group.id}>
            <tr className="border-b border-border">
              <th scope="rowgroup" className={cn(GROUP_LEFT, 'p-0 text-left align-middle')}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  )}
                  <Typography variant="body-semibold">{group.type}</Typography>
                </button>
              </th>
              {columns.map((column) => (
                <td
                  key={`${group.id}-${column.dataIdentifier}-spacer`}
                  className="bg-muted"
                />
              ))}
            </tr>
            {isOpen &&
              group.permissionData.map((entry) => (
                <tr key={entry.id} className="border-b border-border/60 last:border-b-0">
                  <th
                    scope="row"
                    className={cn(ROW_LEFT, 'h-11 pl-10 pr-4 text-left align-middle font-normal')}
                  >
                    <Typography variant="body">{entry.actionLabel}</Typography>
                  </th>
                  {columns.map((column) => {
                    const allowed = Boolean(entry.permissions[column.dataIdentifier]);
                    return (
                      <td
                        key={`${entry.id}-${column.dataIdentifier}`}
                        className="h-11 px-4 text-center align-middle"
                      >
                        <span className="inline-flex items-center justify-center">
                          {allowed ? (
                            <Check
                              className="h-5 w-5 text-success"
                              role="img"
                              aria-label="Allowed"
                            />
                          ) : (
                            <X
                              className="h-5 w-5 text-destructive"
                              role="img"
                              aria-label="Denied"
                            />
                          )}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        );
      })}
    </table>
  );
});

export default RolesPermissionTable;
